import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { GiacomApiService, UserInfo } from '../services/giacomApiService';
import {
  recordLoginEvent,
  getMyVessels,
  Vessel,
  getMyBranding,
  Branding,
} from '../services/adminService';

type AuthContextType = {
  authenticated: boolean;
  user: UserInfo | null;
  userName: string;
  isAdmin: boolean;
  role: string | null;
  /** Cognito Username from the ID token; what the user typed at sign-in. */
  cognitoUsername: string | null;
  token: string | null;
  // KAN-20: assets/vessels the current user is assigned to (scoped server-side).
  // Empty array when none or the fetch fails — never breaks auth.
  vessels: Vessel[];
  // KAN-85: the resolved header branding logo (vessel → org → reseller cascade),
  // or null to fall back to the fixed MIMO logo. Never breaks auth.
  branding: Branding | null;
  logout: () => void;
  login: () => void;
  isLoading: boolean;
  authError: string | null;
  isLoggingOut: boolean;
  resetAuthState: () => void;
  setDisplayName: (name: string) => void;
};

const AuthContext = createContext<AuthContextType>({
  authenticated: false,
  user: null,
  userName: 'User',
  isAdmin: false,
  role: null,
  cognitoUsername: null,
  token: null,
  vessels: [],
  branding: null,
  logout: () => {},
  login: () => {},
  isLoading: true,
  authError: null,
  isLoggingOut: false,
  resetAuthState: () => {},
  setDisplayName: () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [userName, setUserName] = useState('User');
  const [isAdmin, setIsAdmin] = useState(false);
  const [role, setRole] = useState<string | null>(null);
  const [cognitoUsername, setCognitoUsername] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [branding, setBranding] = useState<Branding | null>(null);

  // Guard against setState-after-unmount when the async vessels fetch resolves.
  const isMountedRef = useRef(true);

  // KAN-20: fetch the caller's assigned vessels once the user is established.
  // Fail safe — on error leave vessels empty and never break auth/login.
  const loadMyVessels = useCallback(async () => {
    try {
      const result = await getMyVessels();
      if (isMountedRef.current) {
        setVessels(result);
      }
    } catch (err) {
      console.warn('Failed to load vessels:', err instanceof Error ? err.message : err);
      if (isMountedRef.current) {
        setVessels([]);
      }
    }
  }, []);

  // KAN-85: fetch the resolved header branding logo (vessel → org → reseller).
  // Fail safe — on error leave branding null (header shows the MIMO logo).
  const loadMyBranding = useCallback(async () => {
    try {
      const result = await getMyBranding();
      if (isMountedRef.current) {
        setBranding(result);
      }
    } catch (err) {
      console.warn('Failed to load branding:', err instanceof Error ? err.message : err);
      if (isMountedRef.current) {
        setBranding(null);
      }
    }
  }, []);

  // Generate PKCE code verifier and challenge
  const generateCodeVerifier = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return base64UrlEncode(array);
  };

  const base64UrlEncode = (buffer: Uint8Array) => {
    const base64 = btoa(String.fromCharCode(...buffer));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  };

  const sha256 = async (plain: string) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    return await crypto.subtle.digest('SHA-256', data);
  };

  const generateCodeChallenge = async (verifier: string) => {
    const hashed = await sha256(verifier);
    return base64UrlEncode(new Uint8Array(hashed));
  };

  // Decode JWT to check expiration
  const isTokenExpired = (token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      return Date.now() >= exp;
    } catch (error) {
      return true;
    }
  };

  const extractUserInfo = async (idToken: string, accessToken?: string) => {
    try {
      // Check if ID token is expired
      if (isTokenExpired(idToken)) {
        clearAuthState();
        return false;
      }

      // Store ID token (backend expects user identity claims)
      sessionStorage.setItem('cognito_id_token', idToken);
      // Store access token if provided (needed for Cognito user-facing APIs like ChangePassword)
      if (accessToken) {
        sessionStorage.setItem('cognito_access_token', accessToken);
      }
      setToken(idToken);

      // Fetch user info from Lambda endpoint
      const userInfo = await GiacomApiService.getUserInfo();
      setUser(userInfo);

      // Extract display name
      if (userInfo.username) {
        setUserName(userInfo.username);
      } else if (userInfo.email) {
        setUserName(userInfo.email.split('@')[0]);
      }

      // Extract role from JWT cognito:groups
      try {
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        setCognitoUsername(payload['cognito:username'] || null);
        const groups: string[] = payload['cognito:groups'] || [];
        const userRole = groups.length > 0 ? groups[0] : null;
        setRole(userRole);
        setIsAdmin(userRole === 'super_admin' || userRole === 'reseller_admin');
      } catch {
        setRole(null);
        setCognitoUsername(null);
        setIsAdmin(false);
      }

      return true;
    } catch (error) {
      setAuthError('Failed to fetch user information');
      clearAuthState();
      return false;
    }
  };

  const clearAuthState = () => {
    sessionStorage.removeItem('cognito_id_token');
    sessionStorage.removeItem('cognito_access_token');
    sessionStorage.removeItem('pkce_code_verifier');
    setAuthenticated(false);
    setUser(null);
    setToken(null);
    setUserName('User');
    setIsAdmin(false);
    setRole(null);
    setCognitoUsername(null);
    setVessels([]);
    setBranding(null);
    // Note: Don't reset isLoggingOut here - we need it to stay true during redirect
  };

  const resetAuthState = () => {
    // This is called when we want to fully reset everything including logout flag
    sessionStorage.removeItem('cognito_id_token');
    sessionStorage.removeItem('cognito_access_token');
    sessionStorage.removeItem('pkce_code_verifier');
    setAuthenticated(false);
    setUser(null);
    setToken(null);
    setUserName('User');
    setIsAdmin(false);
    setRole(null);
    setCognitoUsername(null);
    setVessels([]);
    setBranding(null);
    setIsLoggingOut(false);
    setAuthError(null);
    setIsLoading(false);
  };

  const handleCallback = async () => {
    // Check if we're already processing or have processed this callback
    const callbackProcessed = sessionStorage.getItem('callback_processing');
    if (callbackProcessed === 'true') {
      return;
    }

    // Mark as processing to prevent duplicate calls
    sessionStorage.setItem('callback_processing', 'true');

    // Parse URL query string for authorization code
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error');

    // Handle OAuth errors
    if (error) {
      const errorDescription = params.get('error_description') || error;
      setAuthError(errorDescription);
      setIsLoading(false);
      sessionStorage.removeItem('callback_processing');
      return;
    }

    if (!code) {
      setIsLoading(false);
      sessionStorage.removeItem('callback_processing');
      return;
    }

    const codeVerifier = sessionStorage.getItem('pkce_code_verifier');

    if (!codeVerifier) {
      console.error('🔴 handleCallback: Code verifier not found in session storage');
      setAuthError('Authentication session expired. Please try again.');
      setIsLoading(false);
      sessionStorage.removeItem('callback_processing');
      return;
    }

    try {
      // Exchange authorization code for tokens
      const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
      const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
      const redirectUri = process.env.REACT_APP_COGNITO_REDIRECT_URI;

      const tokenRequestBody = {
        grant_type: 'authorization_code',
        client_id: clientId!,
        code: code,
        redirect_uri: redirectUri!,
        code_verifier: codeVerifier,
      };

      const tokenResponse = await fetch(`${cognitoDomain}/oauth2/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(tokenRequestBody).toString(),
      });

      if (!tokenResponse.ok) {
        const errorText = await tokenResponse.text();
        console.error('🔴 handleCallback: Token exchange failed:', errorText);
        console.error('🔴 handleCallback: Response status:', tokenResponse.status);
        setAuthError('Authentication failed. Please try again.');
        setIsLoading(false);
        sessionStorage.removeItem('callback_processing');
        return;
      }

      const tokens = await tokenResponse.json();

      // Use ID token (contains user identity claims like email, username, custom attributes)
      const idToken = tokens.id_token;
      // Access token authorizes user-facing Cognito APIs (e.g. ChangePassword)
      const accessToken = tokens.access_token;

      // Clean up code verifier and processing flag
      sessionStorage.removeItem('pkce_code_verifier');
      sessionStorage.removeItem('callback_processing');

      if (idToken) {
        const success = await extractUserInfo(idToken, accessToken);
        if (success) {
          setAuthenticated(true);
          setAuthError(null);
          scheduleTokenExpiry(idToken);
          // KAN-20: load assigned vessels for logo branding (fire-and-forget).
          loadMyVessels();
          // KAN-85: load the resolved header branding logo (fire-and-forget).
          loadMyBranding();
          // KAN-41: fire-and-forget login audit event (fresh OAuth flow only;
          // session restoration from storage doesn't hit this path).
          recordLoginEvent().catch(err =>
            console.warn('Failed to record login event:', err?.message || err)
          );
        }
      } else {
        console.error('🔴 handleCallback: No ID token in response');
      }

      setIsLoading(false);

      // Clean up URL after successful authentication
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      console.error('🔴 handleCallback: Error during token exchange:', error);
      setAuthError('Network error during authentication. Please try again.');
      setIsLoading(false);
      sessionStorage.removeItem('callback_processing');
    }
  };

  const login = async () => {
    const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    const redirectUri = process.env.REACT_APP_COGNITO_REDIRECT_URI;
    const responseType = process.env.REACT_APP_COGNITO_RESPONSE_TYPE || 'code';
    const scope =
      process.env.REACT_APP_COGNITO_SCOPE || 'email openid profile aws.cognito.signin.user.admin';

    try {
      // Generate PKCE parameters
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      // Store code verifier for later use
      sessionStorage.setItem('pkce_code_verifier', codeVerifier);

      const authUrl = `${cognitoDomain}/oauth2/authorize?client_id=${clientId}&response_type=${responseType}&scope=${encodeURIComponent(
        scope
      )}&redirect_uri=${encodeURIComponent(redirectUri!)}&code_challenge=${codeChallenge}&code_challenge_method=S256`;

      window.location.href = authUrl;
    } catch (error) {
      console.error('Error initiating login:', error);
      setAuthError('Failed to initiate login. Please try again.');
    }
  };

  const logout = () => {
    const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    // Use root URL as logout redirect (must be in Cognito's allowed logout URLs)
    const logoutUri = window.location.origin;

    // Set logging out flag BEFORE clearing auth state
    setIsLoggingOut(true);

    // Clear all auth state
    clearAuthState();

    const logoutUrl = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
      logoutUri
    )}`;
    // Small delay to ensure state updates are processed
    setTimeout(() => {
      window.location.href = logoutUrl;
    }, 100);
  };

  // Timer ref for token expiry check
  const expiryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Schedule auto-logout when token expires
  const scheduleTokenExpiry = useCallback(
    (idToken: string) => {
      // Clear existing timers
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);

      try {
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        const expiresAt = payload.exp * 1000;
        const now = Date.now();
        const msUntilExpiry = expiresAt - now;

        if (msUntilExpiry <= 0) {
          // Already expired
          console.log('Token already expired, logging out');
          logout();
          return;
        }

        // Warn 2 minutes before expiry
        const warningMs = msUntilExpiry - 2 * 60 * 1000;
        if (warningMs > 0) {
          warningTimerRef.current = setTimeout(() => {
            setAuthError('Your session will expire soon. Please save your work.');
          }, warningMs);
        }

        // Auto-logout on expiry
        expiryTimerRef.current = setTimeout(() => {
          console.log('Token expired, auto-logging out');
          logout();
        }, msUntilExpiry);

        console.log(`Session expires in ${Math.round(msUntilExpiry / 60000)} minutes`);
      } catch {
        // Can't parse token, ignore
      }
    },
    // eslint-disable-next-line
    []
  );

  useEffect(() => {
    // Prevent double execution in React StrictMode
    let isCancelled = false;

    const initAuth = async () => {
      if (isCancelled) return;

      // If we're on signed-out page, don't try to authenticate
      if (window.location.pathname === '/signed-out') {
        setIsLoading(false);
        setIsLoggingOut(false);
        setAuthenticated(false);
        return;
      }

      // Check if we're on the callback page
      const isCallback =
        window.location.pathname === '/callback' || window.location.search.includes('code=');

      if (isCallback) {
        // Handle OAuth callback
        await handleCallback();
      } else {
        // Check for existing valid ID token
        const storedIdToken = sessionStorage.getItem('cognito_id_token');

        if (storedIdToken && !isTokenExpired(storedIdToken)) {
          setToken(storedIdToken);
          const success = await extractUserInfo(storedIdToken);
          if (success) {
            setAuthenticated(true);
            scheduleTokenExpiry(storedIdToken);
            // KAN-20: load assigned vessels on session restoration too.
            loadMyVessels();
            // KAN-85: load the resolved header branding logo on restoration too.
            loadMyBranding();
          }
        } else if (storedIdToken) {
          // Token exists but is expired — auto-logout cleanly
          clearAuthState();
        } else {
        }

        setIsLoading(false);
      }
    };

    initAuth();

    // Cleanup timers on unmount
    return () => {
      isCancelled = true;
      isMountedRef.current = false;
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    };
    // eslint-disable-next-line
  }, []);

  const setDisplayName = (name: string) => {
    const trimmed = (name || '').trim();
    if (trimmed) setUserName(trimmed);
  };

  return (
    <AuthContext.Provider
      value={{
        authenticated,
        user,
        userName,
        isAdmin,
        role,
        cognitoUsername,
        token,
        vessels,
        branding,
        logout,
        login,
        isLoading,
        authError,
        isLoggingOut,
        resetAuthState,
        setDisplayName,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
